import { Fragment, memo } from 'react'
import { Typography } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { useGetOrganization } from '@/service-library/hooks/organizations'
import { useGetProjectModel } from '@/service-library/hooks/project-models'
import { useGetWorkflowStateEvent } from '@/service-library/hooks/workflow-state-events'
import { LogEntry } from '@/types/log-entries'
import { WorkflowStateEvent } from '@/types/workflow-state-events'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useWorkflowsContext } from '@/components/workflows-provider/WorkflowsProvider'

const codes = {
  EVENT_TRIGGERED:
    'The {event_name} event for {state_name} in the {workflow_name} workflow was triggered.',
  EVENT_PROCESSED:
    'The {event_name} event for {state_name} in the {workflow_name} workflow was processed.',
  MODEL_PREDICTION_COMPLETE:
    'The {model_name} model using version {model_version} ran prediction.',
  CHIP_UNASSIGNED:
    'A chip with text "{chip_text}" was removed from {field_name}.',
  CHIP_ASSIGNED: 'A chip with text "{chip_text}" was assigned to {field_name}.',
  CHIP_ASSIGNED_TO_DIFFERENT_ROW:
    'A chip with text "{chip_text}" was assigned to {field_name} on a different row.',
  MANUAL_VALUE_SET: '{field_name} was manually set to {manual_value}.',
  MANUAL_VALUE_REMOVED: "{field_name}'s manual value was removed.",
  MANUAL_VALUE_SET_IN_TABLE:
    '{field_name} was manually set to {manual_value} in a row in {parent_field_name}.',
  MANUAL_VALUE_REMOVED_IN_TABLE:
    "{field_name}'s manual value was removed from a row in {parent_field_name}.",
  ORIGINAL_VALUE_SET:
    "{field_name}'s original value was set to {original_value}.",
  ORIGINAL_VALUE_REMOVED: "{field_name}'s original value was removed.",
  ORIGINAL_VALUE_SET_IN_TABLE:
    "{field_name}'s original value was set to {original_value} in a row in {parent_field_name}.",
  ORIGINAL_VALUE_REMOVED_IN_TABLE:
    "{field_name}'s original value was removed from a row in {parent_field_name}.",
  ORG_CHANGE: 'Organization was set to {org_name}.',
  TAG_ADDED: 'The "{tag_name}" tag was added.',
  TAG_REMOVED: 'The "{tag_name}" tag was removed.',
  DOC_STATE_SET:
    'Manually set to {state_name} in the {workflow_name} workflow.',
}

type ObjectInfo = {
  name: string
  color?: string
}

type StateRelatedDescriptionProps = {
  description: string
  stateInfo?: ObjectInfo
  workflowInfo?: ObjectInfo
  eventInfo?: WorkflowStateEvent
}

function StateRelatedDescription({
  description,
  stateInfo,
  workflowInfo,
  eventInfo,
}: StateRelatedDescriptionProps) {
  const parts = description.split(
    /(\{event_name\}|\{state_name\}|\{workflow_name\})/,
  )

  return (
    <>
      {parts.map((part, index) => {
        switch (part) {
          case '{event_name}':
            return <Fragment key={index}>{eventInfo?.name || ''}</Fragment>
          case '{state_name}':
            return stateInfo ? (
              <Typography
                key={index}
                color={stateInfo.color}
                component="span"
                variant="inherit"
              >
                {stateInfo.name || ''}
              </Typography>
            ) : null
          case '{workflow_name}':
            return (
              <Fragment key={index}>
                <b>{workflowInfo?.name || ''}</b>
              </Fragment>
            )
          default:
            return <Fragment key={index}>{part}</Fragment>
        }
      })}
    </>
  )
}

function EventLogEntry({ code, ref_objs, long_description }: LogEntry) {
  const getThemeColor = useGetThemeColor()
  const { workflows } = useWorkflowsContext()
  const workflowStateEventId = ref_objs.find(
    (obj) => obj.key === 'event_name',
  )?.obj_id
  const { workflowStateEvent, isPending } = useGetWorkflowStateEvent({
    id: workflowStateEventId || '',
    filters: {
      fields__only: 'id,name,workflow_state_id',
    },
  })

  if (!workflowStateEvent) return <>{isPending ? '' : long_description}</>

  const workflow = workflows.find((workflow) =>
    workflow.workflow_states?.find(
      (state) => state.id === workflowStateEvent?.workflow_state_id,
    ),
  )
  const state = workflow?.workflow_states?.find(
    (state) => state.id === workflowStateEvent?.workflow_state_id,
  )

  const longDescription = codes[code as keyof typeof codes]
  const props = {
    description: longDescription,
    stateInfo: state
      ? {
          name: state.name || '',
          color: getThemeColor(state.color),
        }
      : undefined,
    workflowInfo: workflow,
    eventInfo: workflowStateEvent,
  }

  return <StateRelatedDescription {...props} />
}

function PredictionLogEntry({ code, ref_objs, long_description }: LogEntry) {
  const { projectModel, isPending } = useGetProjectModel({
    id: ref_objs.find((obj) => obj.key === 'model_name')?.obj_id || '',
    filters: {
      fields__only: 'name,id',
    },
  })

  if (!projectModel) return <>{isPending ? '' : long_description}</>

  let longDescription = codes[code as keyof typeof codes]
  longDescription = longDescription.replaceAll(
    '{model_name}',
    projectModel?.name || '',
  )

  const version = +(
    ref_objs.find((obj) => obj.key === 'model_version')?.obj.version || 0
  )

  longDescription = longDescription.replaceAll(
    '{model_version}',
    `${Number.isInteger(version) ? `${version}.0` : version}`,
  )

  return <>{longDescription}</>
}

function ChipLogEntry({ code, ref_objs, long_description }: LogEntry) {
  const { fieldsMap } = useProjectContext()
  const fieldName =
    fieldsMap[ref_objs.find((obj) => obj.key === 'field_name')?.obj_id || '']
      ?.name || ''

  if (!fieldName) return <>{long_description}</>

  let longDescription = codes[code as keyof typeof codes]
  longDescription = longDescription.replaceAll(
    '{chip_text}',
    ref_objs.find((obj) => obj.key === 'chip_text')?.obj.text || '',
  )
  longDescription = longDescription.replaceAll('{field_name}', fieldName)
  return <>{longDescription}</>
}

function RowValueLogEntry({ code, ref_objs, long_description }: LogEntry) {
  const { fieldsMap } = useProjectContext()
  const fieldName =
    fieldsMap[ref_objs.find((obj) => obj.key === 'field_name')?.obj_id || '']
      ?.name || ''

  if (!fieldName) return <>{long_description}</>

  let longDescription = codes[code as keyof typeof codes]
  longDescription = longDescription.replaceAll('{field_name}', fieldName)

  // Handle manual value replacements
  longDescription = longDescription.replaceAll(
    '{manual_value}',
    ref_objs.find((obj) => obj.key === 'manual_value')?.obj.manual_value || '',
  )

  // Handle original value replacements
  longDescription = longDescription.replaceAll(
    '{original_value}',
    ref_objs.find((obj) => obj.key === 'original_value')?.obj.original_value ||
      '',
  )

  if (code.includes('IN_TABLE')) {
    const parentFieldName =
      fieldsMap[
        ref_objs.find((obj) => obj.key === 'parent_field_name')?.obj_id || ''
      ]?.name || ''
    longDescription = longDescription.replaceAll(
      '{parent_field_name}',
      parentFieldName,
    )
    if (!parentFieldName) return <></>
  }

  return <>{longDescription}</>
}

function OrgLogEntry({ code, ref_objs, long_description }: LogEntry) {
  const { organization, isPending } = useGetOrganization({
    id: ref_objs.find((obj) => obj.key === 'org_name')?.obj_id || '',
    filters: {
      fields__only: 'name,id',
    },
  })

  if (!organization) return <>{isPending ? '' : long_description}</>

  let longDescription = codes[code as keyof typeof codes]

  longDescription = longDescription.replaceAll('{org_name}', organization.name)
  return <>{longDescription}</>
}

function TagEntry({ code, ref_objs }: LogEntry) {
  const { project } = useProjectContext()

  const tagId = ref_objs.find((obj) => obj.key === 'tag_name')?.obj_id
  const tagName = project.tags?.find((tag) => tag.id === tagId)?.name || ''
  let longDescription = codes[code as keyof typeof codes]

  longDescription = longDescription.replaceAll('{tag_name}', tagName)
  return <>{longDescription}</>
}

function DocStateSetEntry({ code, ref_objs }: LogEntry) {
  const getThemeColor = useGetThemeColor()
  const { workflows } = useWorkflowsContext()

  const stateId = ref_objs.find((obj) => obj.key === 'state_name')?.obj_id

  let workflow
  let state

  for (const w of workflows) {
    const s = w.workflow_states?.find((s) => s.id === stateId)
    if (s) {
      workflow = w
      state = s
      break
    }
  }

  const longDescription = codes[code as keyof typeof codes]
  const props = {
    description: longDescription,
    stateInfo: state
      ? {
          name: state.name || '',
          color: getThemeColor(state.color),
        }
      : undefined,
    workflowInfo: workflow,
  }

  return <StateRelatedDescription {...props} />
}

const FIELD_VALUE_CODES = [
  'MANUAL_VALUE_SET',
  'MANUAL_VALUE_REMOVED',
  'MANUAL_VALUE_SET_IN_TABLE',
  'MANUAL_VALUE_REMOVED_IN_TABLE',
  'ORIGINAL_VALUE_SET',
  'ORIGINAL_VALUE_REMOVED',
  'ORIGINAL_VALUE_SET_IN_TABLE',
  'ORIGINAL_VALUE_REMOVED_IN_TABLE',
]

function EntryLongDescription(entry: LogEntry) {
  const { code, long_description } = entry
  if (code === 'EVENT_TRIGGERED' || code === 'EVENT_PROCESSED') {
    return <EventLogEntry {...entry} />
  }
  if (code === 'MODEL_PREDICTION_COMPLETE') {
    return <PredictionLogEntry {...entry} />
  }
  if (
    code === 'CHIP_UNASSIGNED' ||
    code === 'CHIP_ASSIGNED' ||
    code === 'CHIP_ASSIGNED_TO_DIFFERENT_ROW'
  ) {
    return <ChipLogEntry {...entry} />
  }
  if (FIELD_VALUE_CODES.includes(code)) {
    return <RowValueLogEntry {...entry} />
  }

  if (code === 'ORG_CHANGE') {
    return <OrgLogEntry {...entry} />
  }

  if (code === 'TAG_ADDED' || code === 'TAG_REMOVED') {
    return <TagEntry {...entry} />
  }

  if (code === 'DOC_STATE_SET') {
    return <DocStateSetEntry {...entry} />
  }

  return <>{long_description}</>
}

export default memo(EntryLongDescription)
